import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "@/lib/format";
import { Loader2, Search, FileCheck2 } from "lucide-react";

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastOfMonth() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

export default function GeneraPrefatturaDialog({ open, onOpenChange, onCreated }) {
  const [tipo, setTipo] = useState("attiva");
  const [sid, setSid] = useState("");
  const [dal, setDal] = useState(firstOfMonth());
  const [al, setAl] = useState(lastOfMonth());
  const [opts, setOpts] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setSid("");
    Promise.all([api.get("/clienti/options"), api.get("/subvettori/options")]).then(([c, s]) => setOpts({ clienti: c.data, subvettori: s.data }));
  }, [open]);

  const anteprima = async () => {
    if (!sid) {
      toast.error("Seleziona il soggetto");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/prefatture/genera", { tipo, soggetto_id: sid, periodo_dal: dal, periodo_al: al });
      setPreview(data);
      if (data.righe.length === 0) toast.info("Nessun viaggio eseguito da prefatturare nel periodo");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const crea = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prefatture", { ...preview, righe: preview.righe });
      toast.success(`Prefattura ${data.numero} creata in bozza`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="genera-prefattura-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Genera prefattura</DialogTitle>
          <DialogDescription>Raccoglie i viaggi "eseguiti" del periodo non ancora prefatturati e calcola l'importo con il listino del soggetto. La bozza resta modificabile riga per riga.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={tipo} onValueChange={(v) => { setTipo(v); setSid(""); setPreview(null); }}>
              <SelectTrigger className="mt-1 h-9" data-testid="genera-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="attiva">Attiva — cliente</SelectItem>
                <SelectItem value="passiva">Passiva — sub-vettore</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">{tipo === "attiva" ? "Cliente" : "Sub-vettore"}</Label><div className="mt-1"><Combobox options={(tipo === "attiva" ? opts.clienti : opts.subvettori) || []} value={sid} onChange={(v) => { setSid(v); setPreview(null); }} placeholder="Seleziona..." testid="genera-soggetto" /></div></div>
          <div><Label className="text-xs">Dal</Label><Input type="date" className="mt-1 h-9" value={dal} onChange={(e) => setDal(e.target.value)} data-testid="genera-dal" /></div>
          <div><Label className="text-xs">Al</Label><Input type="date" className="mt-1 h-9" value={al} onChange={(e) => setAl(e.target.value)} data-testid="genera-al" /></div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={anteprima} disabled={busy} data-testid="genera-anteprima">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}Anteprima</Button>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="genera-preview">
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div className="border rounded-md p-3 bg-slate-50"><div className="text-xs uppercase text-slate-500">Viaggi</div><div className="text-xl font-head font-bold" data-testid="genera-n-viaggi">{preview.n_viaggi}</div></div>
              <div className="border rounded-md p-3 bg-slate-50"><div className="text-xs uppercase text-slate-500">Imponibile</div><div className="text-xl font-head font-bold">{formatCurrency(preview.imponibile)}</div></div>
              <div className="border rounded-md p-3 bg-slate-50"><div className="text-xs uppercase text-slate-500">Totale con IVA {preview.iva_pct}%</div><div className="text-xl font-head font-bold">{formatCurrency(preview.totale)}</div></div>
              <div className={`border rounded-md p-3 ${preview.n_warning ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50"}`}><div className="text-xs uppercase">Avvisi tariffa</div><div className="text-xl font-head font-bold" data-testid="genera-n-warning">{preview.n_warning}</div></div>
            </div>
            <div className="border border-slate-200 rounded-md max-h-64 overflow-auto text-xs">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1">Data</th><th className="text-left px-2 py-1">Codice</th><th className="text-left px-2 py-1">Tratta</th><th className="text-left px-2 py-1">Calcolo</th><th className="text-right px-2 py-1">Importo</th></tr></thead>
                <tbody>
                  {preview.righe.map((r) => (
                    <tr key={r.viaggio_id} className="border-t border-slate-100">
                      <td className="px-2 py-1 whitespace-nowrap">{formatDate(r.data_viaggio)}</td>
                      <td className="px-2 py-1">{r.codice_viaggio}</td>
                      <td className="px-2 py-1">{r.descrizione}</td>
                      <td className="px-2 py-1 text-slate-600">{r.warning ? <span className="text-amber-700">⚠ {r.warning}</span> : `${r.metodo}: ${r.quantita} × ${r.prezzo_unitario}${r.gasolio_pct ? ` + gasolio ${r.gasolio_pct}%` : ""}${r.supplementi.length ? ` + ${r.supplementi.map((s) => s.codice).join(", ")}` : ""}`}</td>
                      <td className="px-2 py-1 text-right font-medium">{formatCurrency(r.importo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={crea} disabled={busy || preview.righe.length === 0} data-testid="genera-crea">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileCheck2 className="h-4 w-4 mr-1.5" />}Crea bozza ({preview.n_viaggi} viaggi)
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
